import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { useOnlineStatus, offlineCache, pendingActions } from '@/lib/offlineService';
import OfflineIndicator from '@/components/OfflineIndicator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Users,
  UserPlus,
  Calendar,
  Phone,
  User,
  MapPin,
  Loader2,
  CheckCircle,
  ArrowRight,
  WifiOff,
  CloudOff,
  Building2,
  Stethoscope,
  Wallet,
  Send,
  Mail,
  Clock,
  TrendingUp,
  AlertCircle,
  DollarSign,
  Banknote
} from 'lucide-react';
import { toast } from 'sonner';

const SPECIALTIES = [
  'Médecine générale', 'Cardiologie', 'Dermatologie', 'Gynécologie', 
  'Pédiatrie', 'Ophtalmologie', 'Dentiste', 'Kinésithérapie', 'Autre'
];

const FACILITY_TYPES = [
  { value: 'hospital', label: 'Hôpital' },
  { value: 'clinic', label: 'Clinique' },
  { value: 'pharmacy', label: 'Pharmacie' },
  { value: 'laboratory', label: 'Laboratoire' },
  { value: 'imaging_center', label: "Centre d'imagerie" },
  { value: 'health_center', label: 'Centre de santé' }
];

const AgentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [patients, setPatients] = useState([]);
  const [recruits, setRecruits] = useState({ professionals: [], facilities: [] });
  const [commissions, setCommissions] = useState(null);
  
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteType, setInviteType] = useState('professional');
  const [inviting, setInviting] = useState(false);
  
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [requesting, setRequesting] = useState(false);
  
  const [inviteForm, setInviteForm] = useState({
    email: '', full_name: '', phone: '', specialty: '', city: '',
    facility_name: '', facility_type: '', message: ''
  });
  
  const [patientForm, setPatientForm] = useState({
    full_name: '', phone: '', age: '', gender: '', location: ''
  });
  
  const [payoutForm, setPayoutForm] = useState({
    amount: '', payment_method: 'mobile_money', payment_details: ''
  });

  const loadDashboard = useCallback(async () => {
    if (!isOnline) {
      const cached = offlineCache.get('agent_dashboard');
      if (cached) setDashboardData(cached);
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/agent/dashboard');
      setDashboardData(response.data);
      offlineCache.set('agent_dashboard', response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [isOnline]);

  const loadInvitations = useCallback(async () => {
    if (!isOnline) return;
    try {
      const response = await api.get('/agent/invitations');
      setInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  }, [isOnline]);

  const loadPatients = useCallback(async () => {
    if (!isOnline) {
      const cached = offlineCache.get('agent_patients');
      if (cached) setPatients(cached);
      return;
    }
    try {
      const response = await api.get('/agent/patients');
      setPatients(response.data.patients || []);
      offlineCache.set('agent_patients', response.data.patients);
    } catch (error) {
      console.error('Error loading patients:', error);
    }
  }, [isOnline]);

  const loadRecruits = useCallback(async () => {
    if (!isOnline) return;
    try {
      const response = await api.get('/agent/recruits');
      setRecruits(response.data);
    } catch (error) {
      console.error('Error loading recruits:', error);
    }
  }, [isOnline]);

  const loadCommissions = useCallback(async () => {
    if (!isOnline) return;
    try {
      const response = await api.get('/agent/commissions');
      setCommissions(response.data);
    } catch (error) {
      console.error('Error loading commissions:', error);
    }
  }, [isOnline]);

  useEffect(() => {
    loadDashboard();
    loadInvitations();
    loadPatients();
    loadRecruits();
    loadCommissions();
  }, [loadDashboard, loadInvitations, loadPatients, loadRecruits, loadCommissions]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    
    try {
      const endpoint = inviteType === 'professional' 
        ? '/agent/invite-professional' 
        : '/agent/invite-facility';
      
      const payload = inviteType === 'professional' ? {
        email: inviteForm.email,
        full_name: inviteForm.full_name,
        phone: inviteForm.phone,
        specialty: inviteForm.specialty,
        city: inviteForm.city,
        message: inviteForm.message
      } : {
        email: inviteForm.email,
        facility_name: inviteForm.facility_name,
        facility_type: inviteForm.facility_type,
        phone: inviteForm.phone,
        city: inviteForm.city,
        message: inviteForm.message
      };
      
      await api.post(endpoint, payload);
      toast.success('Invitation envoyée !');
      setShowInviteModal(false);
      setInviteForm({ email: '', full_name: '', phone: '', specialty: '', city: '', facility_name: '', facility_type: '', message: '' });
      loadInvitations();
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setInviting(false);
    }
  };

  const handleCreatePatient = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      await api.post('/agent/create-patient', {
        full_name: patientForm.full_name,
        phone: patientForm.phone,
        email: `${patientForm.phone}@dockhta-patient.cm`,
        password: '123456',
        age: patientForm.age ? parseInt(patientForm.age) : null,
        gender: patientForm.gender,
        location: patientForm.location,
        role: 'patient'
      });
      toast.success('Patient créé !');
      setShowPatientModal(false);
      setPatientForm({ full_name: '', phone: '', age: '', gender: '', location: '' });
      loadPatients();
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleRequestPayout = async (e) => {
    e.preventDefault();
    setRequesting(true);
    
    try {
      await api.post('/agent/commissions/request', {
        amount: parseFloat(payoutForm.amount),
        payment_method: payoutForm.payment_method,
        payment_details: payoutForm.payment_details
      });
      toast.success('Demande de retrait envoyée !');
      setShowPayoutModal(false);
      setPayoutForm({ amount: '', payment_method: 'mobile_money', payment_details: '' });
      loadCommissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setRequesting(false);
    }
  };

  const openInviteModal = (type) => {
    setInviteType(type);
    setInviteForm({ email: '', full_name: '', phone: '', specialty: '', city: '', facility_name: '', facility_type: '', message: '' });
    setShowInviteModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <div className="section-padding py-6" data-testid="agent-dashboard">
      <OfflineIndicator />
      
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Agent Terrain
            </h1>
            <p className="text-slate-600 text-sm mt-1">Bienvenue, {user?.full_name}</p>
          </div>
          {!isOnline && (
            <Badge className="bg-amber-100 text-amber-700 gap-1">
              <WifiOff className="w-3 h-3" />
              Hors ligne
            </Badge>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.patients_count || 0}</p>
                <p className="text-xs text-slate-500">Patients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.professionals_count || 0}</p>
                <p className="text-xs text-slate-500">Professionnels</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.facilities_count || 0}</p>
                <p className="text-xs text-slate-500">Établissements</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{(stats.pending_commission || 0).toLocaleString()}</p>
                <p className="text-xs text-white/80">FCFA à retirer</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-4 px-4">
          <TabsList className="inline-flex min-w-max mb-4">
            <TabsTrigger value="dashboard" className="text-xs sm:text-sm">
              <TrendingUp className="w-4 h-4 mr-1 sm:mr-2" />
              Tableau de bord
            </TabsTrigger>
            <TabsTrigger value="patients" className="text-xs sm:text-sm">
              <Users className="w-4 h-4 mr-1 sm:mr-2" />
              Patients
            </TabsTrigger>
            <TabsTrigger value="recruits" className="text-xs sm:text-sm">
              <UserPlus className="w-4 h-4 mr-1 sm:mr-2" />
              Recrues
            </TabsTrigger>
            <TabsTrigger value="commissions" className="text-xs sm:text-sm">
              <Wallet className="w-4 h-4 mr-1 sm:mr-2" />
              Commissions
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Actions rapides</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  className="w-full justify-start bg-blue-500 hover:bg-blue-600"
                  onClick={() => setShowPatientModal(true)}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Créer un patient
                </Button>
                <Button 
                  className="w-full justify-start bg-green-500 hover:bg-green-600"
                  onClick={() => openInviteModal('professional')}
                >
                  <Stethoscope className="w-4 h-4 mr-2" />
                  Inviter un professionnel
                </Button>
                <Button 
                  className="w-full justify-start bg-purple-500 hover:bg-purple-600"
                  onClick={() => openInviteModal('facility')}
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Inviter un établissement
                </Button>
              </CardContent>
            </Card>

            {/* Pending Invitations */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  Invitations en attente
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invitations.filter(i => i.status === 'pending').length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-4">Aucune invitation en attente</p>
                ) : (
                  <div className="space-y-2">
                    {invitations.filter(i => i.status === 'pending').slice(0, 5).map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          {inv.type === 'professional' ? (
                            <Stethoscope className="w-4 h-4 text-green-500" />
                          ) : (
                            <Building2 className="w-4 h-4 text-purple-500" />
                          )}
                          <span className="text-sm font-medium">
                            {inv.full_name || inv.facility_name}
                          </span>
                        </div>
                        <Badge variant="outline" className="text-xs">En attente</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Commission Summary */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  Récapitulatif des commissions
                </CardTitle>
                <CardDescription>20% sur chaque abonnement de vos recrues</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">
                      {(commissions?.stats?.total_earned || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">Total gagné (FCFA)</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {(commissions?.stats?.pending_amount || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">Disponible (FCFA)</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">
                      {(commissions?.stats?.paid_amount || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">Déjà retiré (FCFA)</p>
                  </div>
                </div>
                {(commissions?.stats?.pending_amount || 0) >= 1000 && (
                  <Button 
                    className="w-full mt-4 bg-red-500 hover:bg-red-600"
                    onClick={() => setShowPayoutModal(true)}
                  >
                    <Banknote className="w-4 h-4 mr-2" />
                    Demander un retrait
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Patients Tab */}
        <TabsContent value="patients">
          <Button 
            className="w-full mb-4 bg-red-500 hover:bg-red-600"
            onClick={() => setShowPatientModal(true)}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Créer un nouveau patient
          </Button>
          
          {patients.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-600">Aucun patient</h3>
                <p className="text-sm text-slate-500">Créez votre premier patient</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {patients.map((patient) => (
                <Card key={patient.id} className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{patient.full_name}</h3>
                          <p className="text-sm text-slate-500">{patient.phone}</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => navigate('/search')}>
                        Réserver RDV
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Recruits Tab */}
        <TabsContent value="recruits">
          <div className="flex gap-2 mb-4">
            <Button onClick={() => openInviteModal('professional')} className="flex-1 bg-green-500 hover:bg-green-600">
              <Stethoscope className="w-4 h-4 mr-2" />
              Inviter professionnel
            </Button>
            <Button onClick={() => openInviteModal('facility')} className="flex-1 bg-purple-500 hover:bg-purple-600">
              <Building2 className="w-4 h-4 mr-2" />
              Inviter établissement
            </Button>
          </div>

          {/* Invitations */}
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <Send className="w-4 h-4" />
            Invitations envoyées ({invitations.length})
          </h3>
          {invitations.length === 0 ? (
            <Card className="mb-6">
              <CardContent className="py-8 text-center">
                <Mail className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">Aucune invitation envoyée</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2 mb-6">
              {invitations.map((inv) => (
                <Card key={inv.id} className="border-0 shadow-sm">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {inv.type === 'professional' ? (
                          <Stethoscope className="w-4 h-4 text-green-500" />
                        ) : (
                          <Building2 className="w-4 h-4 text-purple-500" />
                        )}
                        <div>
                          <p className="font-medium text-sm">{inv.full_name || inv.facility_name}</p>
                          <p className="text-xs text-slate-500">{inv.email}</p>
                        </div>
                      </div>
                      <Badge className={
                        inv.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        inv.status === 'registered' ? 'bg-green-100 text-green-700' :
                        'bg-slate-100 text-slate-500'
                      }>
                        {inv.status === 'pending' ? 'En attente' : inv.status === 'registered' ? 'Inscrit' : 'Expiré'}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Active Recruits */}
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            Recrues actives ({recruits.professionals.length + recruits.facilities.length})
          </h3>
          {recruits.professionals.length === 0 && recruits.facilities.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <UserPlus className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">Aucune recrue pour le moment</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {recruits.professionals.map((pro) => (
                <Card key={pro.id} className="border-0 shadow-sm border-l-4 border-l-green-500">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <Stethoscope className="w-4 h-4 text-green-500" />
                      <div>
                        <p className="font-medium text-sm">{pro.full_name}</p>
                        <p className="text-xs text-slate-500">{pro.specialty} • {pro.city}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {recruits.facilities.map((fac) => (
                <Card key={fac.id} className="border-0 shadow-sm border-l-4 border-l-purple-500">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-purple-500" />
                      <div>
                        <p className="font-medium text-sm">{fac.full_name}</p>
                        <p className="text-xs text-slate-500">{fac.facility_type}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Commissions Tab */}
        <TabsContent value="commissions">
          {/* Tarifs */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Card className="border-0 shadow-sm bg-green-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">5 000 FCFA</p>
                <p className="text-xs text-green-700">Par inscription</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm bg-blue-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">20%</p>
                <p className="text-xs text-blue-700">Commission mensuelle</p>
              </CardContent>
            </Card>
          </div>

          <Card className="mb-4 bg-gradient-to-r from-red-500 to-red-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm">Solde disponible</p>
                  <p className="text-3xl font-bold">{(commissions?.stats?.pending_amount || 0).toLocaleString()} FCFA</p>
                </div>
                <Button 
                  variant="secondary"
                  disabled={(commissions?.stats?.pending_amount || 0) < 1000}
                  onClick={() => setShowPayoutModal(true)}
                >
                  <Banknote className="w-4 h-4 mr-2" />
                  Retirer
                </Button>
              </div>
              <p className="text-white/60 text-xs mt-2">Minimum de retrait: 1,000 FCFA</p>
            </CardContent>
          </Card>

          <h3 className="font-semibold mb-2">Historique des commissions</h3>
          {(commissions?.commissions || []).length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Wallet className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">Aucune commission pour le moment</p>
                <p className="text-xs text-slate-400 mt-1">
                  Invitez des professionnels et établissements pour gagner:
                </p>
                <div className="flex justify-center gap-4 mt-3">
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">5 000 FCFA / inscription</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">20% / abonnement mensuel</span>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {commissions.commissions.map((comm) => (
                <Card key={comm.id} className="border-0 shadow-sm">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{comm.source_name}</p>
                        <p className="text-xs text-slate-500">{new Date(comm.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">+{comm.amount.toLocaleString()} FCFA</p>
                        <Badge className={comm.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                          {comm.status === 'pending' ? 'Disponible' : 'Retiré'}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {inviteType === 'professional' ? 'Inviter un professionnel' : 'Inviter un établissement'}
            </DialogTitle>
            <DialogDescription>
              Envoyez une invitation par email. Vous gagnerez <strong className="text-green-600">5 000 FCFA</strong> à l'inscription + <strong className="text-blue-600">20%</strong> sur leurs abonnements mensuels.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleInvite} className="space-y-4 mt-4">
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            
            {inviteType === 'professional' ? (
              <>
                <div>
                  <Label>Nom complet *</Label>
                  <Input
                    value={inviteForm.full_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Spécialité</Label>
                  <Select value={inviteForm.specialty} onValueChange={(v) => setInviteForm({ ...inviteForm, specialty: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>
                      {SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label>Nom de l&apos;établissement *</Label>
                  <Input
                    value={inviteForm.facility_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, facility_name: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Type d&apos;établissement *</Label>
                  <Select value={inviteForm.facility_type} onValueChange={(v) => setInviteForm({ ...inviteForm, facility_type: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>
                      {FACILITY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={inviteForm.phone}
                  onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Ville</Label>
                <Input
                  value={inviteForm.city}
                  onChange={(e) => setInviteForm({ ...inviteForm, city: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Message personnalisé</Label>
              <Textarea
                value={inviteForm.message}
                onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                placeholder="Ajoutez un message personnel..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowInviteModal(false)} className="flex-1">
                Annuler
              </Button>
              <Button type="submit" disabled={inviting} className="flex-1 bg-red-500 hover:bg-red-600">
                {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Envoyer</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Patient Modal */}
      <Dialog open={showPatientModal} onOpenChange={setShowPatientModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Créer un patient</DialogTitle>
            <DialogDescription>Mot de passe par défaut: 123456</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreatePatient} className="space-y-4 mt-4">
            <div>
              <Label>Nom complet *</Label>
              <Input
                value={patientForm.full_name}
                onChange={(e) => setPatientForm({ ...patientForm, full_name: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Téléphone *</Label>
              <Input
                value={patientForm.phone}
                onChange={(e) => setPatientForm({ ...patientForm, phone: e.target.value })}
                required
                placeholder="6XXXXXXXX"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Âge</Label>
                <Input
                  type="number"
                  value={patientForm.age}
                  onChange={(e) => setPatientForm({ ...patientForm, age: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Genre</Label>
                <Select value={patientForm.gender} onValueChange={(v) => setPatientForm({ ...patientForm, gender: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Homme</SelectItem>
                    <SelectItem value="female">Femme</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Localisation</Label>
              <Input
                value={patientForm.location}
                onChange={(e) => setPatientForm({ ...patientForm, location: e.target.value })}
                placeholder="Quartier, ville"
                className="mt-1"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowPatientModal(false)} className="flex-1">
                Annuler
              </Button>
              <Button type="submit" disabled={creating} className="flex-1 bg-red-500 hover:bg-red-600">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payout Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Demander un retrait</DialogTitle>
            <DialogDescription>
              Solde disponible: {(commissions?.stats?.pending_amount || 0).toLocaleString()} FCFA
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleRequestPayout} className="space-y-4 mt-4">
            <div>
              <Label>Montant (FCFA) *</Label>
              <Input
                type="number"
                value={payoutForm.amount}
                onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })}
                min="1000"
                max={commissions?.stats?.pending_amount || 0}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Méthode de paiement *</Label>
              <Select value={payoutForm.payment_method} onValueChange={(v) => setPayoutForm({ ...payoutForm, payment_method: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mobile_money">Mobile Money (Orange/MTN)</SelectItem>
                  <SelectItem value="bank_transfer">Virement bancaire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{payoutForm.payment_method === 'mobile_money' ? 'Numéro Mobile Money *' : 'RIB / Numéro de compte *'}</Label>
              <Input
                value={payoutForm.payment_details}
                onChange={(e) => setPayoutForm({ ...payoutForm, payment_details: e.target.value })}
                placeholder={payoutForm.payment_method === 'mobile_money' ? '6XXXXXXXX' : 'Numéro de compte'}
                required
                className="mt-1"
              />
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                Votre demande sera traitée par l&apos;administration sous 48h ouvrées.
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowPayoutModal(false)} className="flex-1">
                Annuler
              </Button>
              <Button type="submit" disabled={requesting} className="flex-1 bg-red-500 hover:bg-red-600">
                {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Demander'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentDashboard;
